import { ChangeDetectionStrategy, Component, effect, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { NotificationService } from '@iot-platform/notification';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, of, tap } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { AUTH_CONFIG, AuthConfig } from '../../auth.config';
import { SsoCodeToTokensResponse } from '../../models/sso-code-to-tokens-response';
import { AuthService } from '../../services/auth.service';
import { AuthPageActions } from '../../state/actions/auth-page.actions';
import { AuthFacade } from '../../state/facades';

@Component({
  imports: [MatProgressSpinner, TranslatePipe],
  selector: 'iot-platform-login-callback',
  templateUrl: './login-callback.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./login-callback.component.scss', '../../containers/login-shell/login-shell.component.scss', '../../scss/style.scss']
})
export class LoginCallbackComponent {
  tokens$: Observable<SsoCodeToTokensResponse | null>;
  tokens: Signal<SsoCodeToTokensResponse | null | undefined>;
  protected readonly authConfig: AuthConfig = inject(AUTH_CONFIG);
  protected notificationService: NotificationService = inject(NotificationService);
  protected authService: AuthService = inject(AuthService);
  protected translate: TranslateService = inject(TranslateService);
  protected route: ActivatedRoute = inject(ActivatedRoute);
  protected store: Store = inject(Store);
  protected authFacade: AuthFacade = inject(AuthFacade);

  constructor() {
    this.tokens$ = this.route.queryParams.pipe(
      tap(() => this.notificationService.showLoader()),
      switchMap((queryParams) =>
        this.authService.getSsoTokensFromCode(queryParams?.code).pipe(
          catchError(() => {
            this.notificationService.hideLoader();
            this.notificationService.displayError(this.translate.instant('LOGIN.AUTHENTICATION_FAILED'));
            this.authFacade.signOut();
            return of(null);
          })
        )
      ),
      finalize(() => this.notificationService.hideLoader())
    );
    this.tokens = toSignal(this.tokens$);

    effect(() => {
      const tokens = this.tokens();
      if (tokens) {
        const { idToken, accessToken, refreshToken, tokenType, expiresIn } = tokens;
        this.authService.storeSsoTokens({
          idToken,
          accessToken,
          tokenType,
          refreshToken,
          expiresIn: String(expiresIn)
        });
        this.store.dispatch(AuthPageActions.validateSsoTokens({ idToken, accessToken, refreshToken, expiresIn }));
      }
    });
  }
}
